import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sparkles, ArrowRight, Users, TrendingUp } from "lucide-react";
import { STATS, LOGOS } from "@/data/content";
import { useEnroll } from "@/context/EnrollContext";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.6, ease: [0.4, 0, 0.2, 1] } }),
};

export const Hero = () => {
  const { openEnroll } = useEnroll();

  return (
    <section id="home" className="relative overflow-hidden bg-background pt-24 pb-0 lg:pt-32">
      {/* Subtle top glow */}
      <div className="glow-orb absolute -top-32 left-1/2 h-[340px] w-[600px] -translate-x-1/2 opacity-50" />

      <div className="relative mx-auto grid max-w-7xl items-center gap-10 px-5 lg:grid-cols-2 lg:gap-16 lg:px-8">
        {/* Left copy */}
        <div className="pb-16 lg:pb-24">
          <motion.div initial="hidden" animate="visible" custom={0} variants={fadeUp}>
            <Badge variant="outline" className="gap-2 border-primary/40 bg-primary/10 px-3 py-1.5 text-primary font-semibold">
              <Sparkles className="h-3.5 w-3.5" />
              India's Top Executive Career Coach
            </Badge>
          </motion.div>

          <motion.h1
            initial="hidden" animate="visible" custom={1} variants={fadeUp}
            className="mt-5 font-display text-4xl font-bold leading-tight tracking-tight text-foreground sm:text-5xl lg:text-[3.5rem]"
          >
            Meet CHINI: Your Always On{" "}
            <span className="text-gradient-primary">AI Interview Coach</span>
          </motion.h1>

          <motion.p
            initial="hidden" animate="visible" custom={2} variants={fadeUp}
            className="mt-5 max-w-lg text-base leading-relaxed text-muted-foreground sm:text-lg"
          >
            Practice real interview questions, sharpen your answers, and get personalized guidance anytime with a Hybrid AI Interview Coach built on 20+ years of executive coaching expertise.
          </motion.p>

          <motion.div
            initial="hidden" animate="visible" custom={3} variants={fadeUp}
            className="mt-8 flex flex-col gap-3 sm:flex-row"
          >
            <Button variant="hero" size="xl" onClick={() => openEnroll("Professional")} className="group text-base px-8">
              Start Your Journey
              <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
            </Button>

          </motion.div>

          {/* Social proof row */}
          <motion.div
            initial="hidden" animate="visible" custom={4} variants={fadeUp}
            className="mt-8 flex items-center gap-5 flex-wrap"
          >
            <div className="flex items-center gap-2">
              <div className="flex -space-x-2">
                {["#f97316", "#fb923c", "#ea580c", "#fdba74"].map((c, i) => (
                  <span key={i} className="h-8 w-8 rounded-full border-2 border-white" style={{ background: c }} />
                ))}
              </div>
              <span className="text-sm text-muted-foreground"><span className="font-semibold text-foreground">2,000+</span> Professionals</span>
            </div>
            <div className="h-5 w-px bg-border" />
            <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
              <TrendingUp className="h-4 w-4 text-primary" />
              <span><span className="font-semibold text-foreground">2 Million+</span> Digital Footprint</span>
            </div>
          </motion.div>

          {/* Trusted logo badges */}
          <motion.div initial="hidden" animate="visible" custom={5} variants={fadeUp} className="mt-8">
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-3 font-medium">Trusted by Professionals From</p>
            <div className="flex flex-wrap items-center gap-3">
              {[
                "/logos/logo-1.png",
                "/logos/logo-2.png",
                "/logos/logo-3.png",
                "/logos/logo-4.png",
                "/logos/logo-5.png",
                "/logos/logo-6.png",
                "/logos/logo-7.png",
                "/logos/logo-8.png",
              ].map((src, idx) => (
                <div
                  key={idx}
                  className="flex h-12 w-28 items-center justify-center rounded-xl bg-[#F6F6F6] px-3 py-1.5 transition-transform hover:scale-105"
                >
                  <img
                    src={src}
                    alt={`Alumni company logo ${idx + 1}`}
                    className="max-w-full object-contain"
                  />
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Right visual */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, x: 30 }}
          animate={{ opacity: 1, scale: 1, x: 0 }}
          transition={{ duration: 0.8, ease: [0.4, 0, 0.2, 1] }}
          className="relative hidden lg:block"
        >
          <div className="relative overflow-hidden rounded-3xl shadow-2xl">
            <img
              src="https://images.unsplash.com/flagged/photo-1575227057258-50cb9bffb1af"
              alt="Professional career coaching"
              className="h-[580px] w-full object-cover object-top"
              loading="eager"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-foreground/20 via-transparent to-transparent" />
          </div>

          {/* Floating card - top left */}
          <motion.div
            className="absolute -left-8 top-12 rounded-2xl bg-white border border-border shadow-lg p-4"
            animate={{ y: [0, -8, 0] }}
            transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut" }}
          >
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-primary">
                <TrendingUp className="h-5 w-5 text-white" />
              </span>
              <div>
                <p className="text-xs text-muted-foreground">Avg. Salary Increase</p>
                <p className="text-sm font-bold text-foreground">40%</p>
              </div>
            </div>
          </motion.div>

          {/* Floating card - bottom right */}
          <motion.div
            className="absolute -right-6 bottom-16 rounded-2xl bg-white border border-border shadow-lg p-4"
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 5.5, repeat: Infinity, ease: "easeInOut" }}
          >
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-primary">
                <Users className="h-5 w-5 text-white" />
              </span>
              <div>
                <p className="text-xs text-muted-foreground">Dream Job</p>
                <p className="text-sm font-bold text-foreground">Offer Accepted 🎉</p>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* Stats bar - solid orange background like screenshot */}
      <div className="relative mt-16 bg-primary">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="grid grid-cols-2 gap-0 sm:grid-cols-4">
            {STATS.map((s, i) => (
              <div
                key={s.label}
                className={`py-8 px-6 text-center ${i < STATS.length - 1 ? "border-r border-white/20" : ""}`}
              >
                <p className="font-display text-3xl font-bold text-white sm:text-4xl">{s.value}</p>
                <p className="mt-1 text-sm text-white/80">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
